// Admin Engagement Management Page (Volunteers & Sponsorships)
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import DataTable from '../../components/admin/DataTable';
import LoadingSpinner from '../../components/admin/LoadingSpinner';

const AdminEngagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'volunteers' | 'sponsors'>('volunteers');
  const [volunteers, setVolunteers] = useState<any[]>([]);
  const [sponsors, setSponsors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      if (activeTab === 'volunteers') {
        const response = await fetch('http://localhost:5000/api/volunteers/applications');
        const data = await response.json();
        setVolunteers(Array.isArray(data) ? data : data.applications || []);
      } else {
        const response = await fetch('http://localhost:5000/api/event-sponsorships');
        const data = await response.json();
        setSponsors(Array.isArray(data) ? data : data.requests || data.eventRequests || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (id: number, status: string) => {
    try {
      const endpoint = activeTab === 'volunteers' 
        ? `volunteers/applications/${id}/status`
        : `event-sponsorships/${id}/status`;
      await fetch(`http://localhost:5000/api/${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const volunteerColumns = [
    { key: 'full_name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'contact_number', label: 'Phone' },
    {
      key: 'skills',
      label: 'Skills',
      render: (value: string) => value?.substring(0, 30) + '...' || 'N/A'
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            background: value === 'pending' ? '#FED7AA' : value === 'approved' ? '#D1FAE5' : '#FEE2E2',
            color: value === 'pending' ? '#9A3412' : value === 'approved' ? '#065F46' : '#991B1B',
            border: value === 'pending' ? '1px solid #FDBA74' : value === 'approved' ? '1px solid #A7F3D0' : '1px solid #FCA5A5'
          }}
        >
          {value}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, row: any) => (
        <button
          onClick={() => {
            setSelectedApplication(row);
            setShowDetailsModal(true);
          }}
          style={{
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #3B82F6',
            background: '#EFF6FF',
            color: '#1D4ED8',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          View Details
        </button>
      )
    }
  ];

  const sponsorColumns = [
    { key: 'sponsor_name', label: 'Sponsor Name', sortable: true },
    { key: 'sponsor_email', label: 'Email', sortable: true },
    { key: 'event_name', label: 'Event', sortable: true },
    {
      key: 'sponsorship_amount',
      label: 'Amount',
      render: (value: number) => `$${value?.toLocaleString() || 0}`
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            background: value === 'pending' ? '#FED7AA' : value === 'approved' ? '#D1FAE5' : '#FEE2E2',
            color: value === 'pending' ? '#9A3412' : value === 'approved' ? '#065F46' : '#991B1B',
            border: value === 'pending' ? '1px solid #FDBA74' : value === 'approved' ? '1px solid #A7F3D0' : '1px solid #FCA5A5'
          }}
        >
          {value}
        </span>
      )
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ color: '#111827', fontSize: '26px', fontWeight: '700', marginBottom: '6px', letterSpacing: '-0.02em' }}>
          Engagement Management
        </h1>
        <p style={{ color: '#6B7280', fontSize: '14px', fontWeight: '500' }}>
          Manage volunteer applications and event sponsorships
        </p>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', gap: '8px' }}>
        <button
          onClick={() => setActiveTab('volunteers')}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: activeTab === 'volunteers' ? 'none' : '1px solid #E5E7EB',
            background: activeTab === 'volunteers' ? '#111827' : '#FFFFFF',
            color: activeTab === 'volunteers' ? '#FFFFFF' : '#6B7280',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Volunteers ({volunteers.length})
        </button>
        <button
          onClick={() => setActiveTab('sponsors')}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: activeTab === 'sponsors' ? 'none' : '1px solid #E5E7EB',
            background: activeTab === 'sponsors' ? '#111827' : '#FFFFFF',
            color: activeTab === 'sponsors' ? '#FFFFFF' : '#6B7280',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Sponsorships ({sponsors.length})
        </button>
      </div>

      <DataTable
        columns={activeTab === 'volunteers' ? volunteerColumns : sponsorColumns}
        data={activeTab === 'volunteers' ? volunteers : sponsors}
        actions={(row) => (
          <>
            {row.status === 'pending' && (
              <>
                <button
                  onClick={() => updateStatus(row.id, 'approved')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1FAE5',
                    background: '#ECFDF5',
                    color: '#059669',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Approve
                </button>
                <button
                  onClick={() => updateStatus(row.id, 'rejected')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #FEE2E2',
                    background: '#FEF2F2',
                    color: '#DC2626',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Reject
                </button>
              </>
            )}
            {row.status !== 'pending' && (
              <button
                onClick={() => updateStatus(row.id, 'pending')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #FED7AA',
                  background: '#FEF3C7',
                  color: '#92400E',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Reset to Pending
              </button>
            )}
          </>
        )}
      />

      {/* Application Details Modal */}
      {showDetailsModal && selectedApplication && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ color: '#111827', fontSize: '20px', fontWeight: '700' }}>
                Volunteer Application Details
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6B7280'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Full Name
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.full_name}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Date of Birth
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {new Date(selectedApplication.date_of_birth).toLocaleDateString()}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Email
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.email}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Contact Number
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.contact_number}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Gender
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.gender}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Education Level
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.education_level || 'N/A'}
                </p>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Address
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.address}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Available Days
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {Array.isArray(selectedApplication.available_days) 
                    ? selectedApplication.available_days.join(', ') 
                    : selectedApplication.available_days}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Preferred Time
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {Array.isArray(selectedApplication.preferred_time) 
                    ? selectedApplication.preferred_time.join(', ') 
                    : selectedApplication.preferred_time}
                </p>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Areas of Interest
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {Array.isArray(selectedApplication.areas_of_interest) 
                    ? selectedApplication.areas_of_interest.join(', ') 
                    : selectedApplication.areas_of_interest}
                  {selectedApplication.other_area && ` (Other: ${selectedApplication.other_area})`}
                </p>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Skills
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.skills || 'N/A'}
                </p>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Languages
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.languages || 'N/A'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Has Experience?
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.has_experience === 'Yes' || selectedApplication.has_experience === true || selectedApplication.has_experience === 1 ? 'Yes' : 'No'}
                </p>
              </div>

              {(selectedApplication.has_experience === 'Yes' || selectedApplication.has_experience === true || selectedApplication.has_experience === 1) && (
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                    Experience Details
                  </label>
                  <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                    {selectedApplication.experience_details}
                  </p>
                </div>
              )}

              <div style={{ gridColumn: 'span 2', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
                <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  Emergency Contact
                </h3>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Name
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.emergency_name}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Relationship
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.emergency_relationship}
                </p>
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                  Contact Number
                </label>
                <p style={{ color: '#111827', fontSize: '14px', fontWeight: '500' }}>
                  {selectedApplication.emergency_contact}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            {selectedApplication.status === 'pending' && (
              <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    updateStatus(selectedApplication.id, 'rejected');
                    setShowDetailsModal(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid #FEE2E2',
                    background: '#FEF2F2',
                    color: '#DC2626',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Reject Application
                </button>
                <button
                  onClick={() => {
                    updateStatus(selectedApplication.id, 'approved');
                    setShowDetailsModal(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#10B981',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Approve Application
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminEngagement;
